<script setup>
import {
  getCategoryImageByKey,
  OHENG_COLORS,
  OHENG_HANJA,
  RELATION_TYPES,
  LAYOUT_POSITIONS,
} from './constants';
import QuoteIcon from '@manseImages/harmony/quote-left.svg';
import LineSaeng from '@manseImages/harmony/legend/line-saeng.svg';
import LineGeuk from '@manseImages/harmony/legend/line-geuk.svg';
import LineBigyeon from '@manseImages/harmony/legend/line-bigyeon.svg';
import IconScales from '@manseImages/harmony/icons/icon-scales.png';
import IconPuzzle from '@manseImages/harmony/icons/icon-puzzle.png';
import IconFlashOn from '@manseImages/harmony/icons/icon-flash-on.png';
import IconSignpost from '@manseImages/harmony/icons/icon-signpost.png';
import IconMagnetic from '@manseImages/harmony/icons/icon-magnetic.png';
import IconOceanWave from '@manseImages/harmony/icons/icon-ocean-wave.png';
import IconProcess from '@manseImages/harmony/icons/icon-process.png';
import IconHeart from '@manseImages/harmony/icons/icon-heart.png';
import IconCheckmark from '@manseImages/harmony/icons/icon-checkmark.png';
import IconCancel from '@manseImages/harmony/icons/icon-cancel.png';
import IconChatBubble from '@manseImages/harmony/icons/icon-chat-bubble.png';
import IconSoccerBall from '@manseImages/harmony/icons/icon-soccer-ball.png';
import IconMusic from '@manseImages/harmony/icons/icon-music.png';
import IconMonitor from '@manseImages/harmony/icons/icon-monitor.png';
import IconTrust from '@manseImages/harmony/icons/icon-trust.png';
import IconTeddyBear from '@manseImages/harmony/icons/icon-teddy-bear.png';
import IconMarketShare from '@manseImages/harmony/icons/icon-market-share.png';
import IconHeartArrow from '@manseImages/harmony/icons/icon-heart-arrow.png';
import IconHighFive from '@manseImages/harmony/icons/icon-high-five.png';
import IconDecision from '@manseImages/harmony/icons/icon-decision.svg';
import { DEV_MODE } from '@/config/dev-mode';

const { VITE_WEB_BASE_URL } = import.meta.env;

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  hData: Object,
  userName: String,
  categoryId: {
    type: String,
    default: 'lover',
  },
  hasPermission: Boolean,
  /** 모바일 페이지 전환 시 현재 보이는 쪽 ('left' | 'right' | undefined) */
  activeSide: String,
});

const emit = defineEmits(['viewCategory']);

/** 카테고리별 타이틀/이모지 매핑 */
const CATEGORY_LABEL = {
  LOVER: { emoji: '💖', label: '연인 관계' },
  FRIEND: { emoji: '🤝', label: '친구 관계' },
  BUSINESS: { emoji: '💼', label: '비즈니스 관계' },
  FAMILY: { emoji: '🏠', label: '가족 관계' },
};
const categoryLabel = computed(() => CATEGORY_LABEL[props.categoryId?.toUpperCase()] || CATEGORY_LABEL.LOVER);

/** 뷰 모드 토글: character / oheng */
const viewMode = ref('character');
watch(viewMode, () => {
  arrowKey.value++;
});

/** 카테고리별 일러스트 이미지 — persons 정보 기반 결정적 선택 */
const randomImage = computed(() => {
  const key = (props.hData?.persons || [])
    .map((p) => `${p.name || ''}-${p.birthInfo || ''}-${p.gender || ''}`)
    .join('|');
  return getCategoryImageByKey(props.categoryId?.toLowerCase(), key);
});

/** 생극 범례 클릭 → 낭만센터 상생상극 글 새 탭 (앱은 외부 브라우저) */
const SANGSAENG_ARTICLE_URL = DEV_MODE
  ? 'https://d25web.thegam.io/nangman-center/word/7578'
  : 'https://nangmancalendar.com/nangman-center/word/9360?cate=4681';
const openSangsaengArticle = () => {
  if (window?.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({
      action: 'openExternal',
      payload: { url: SANGSAENG_ARTICLE_URL },
    }));
  } else {
    window.open(SANGSAENG_ARTICLE_URL, '_blank');
  }
};

/** 한줄 요약 */
const summaryText = computed(() => {
  return props.hData?.한줄요약 || '';
});

/** ── 동적 인물 목록 ── */
const personCount = computed(() => {
  return props.hData?.persons?.length || 2;
});

const persons = computed(() => {
  const src = props.hData?.persons || [];
  return src.map((d) => {
    const ohengColor = OHENG_COLORS[d.oheng] || {};
    return {
      name: d.name,
      gender: d.gender,
      age: d.age,
      birthInfo: d.birthInfo,
      oheng: d.oheng,
      ilgan: d.ilgan,
      ilganLabel: d.ilganLabel,
      ilganHanja: d.ilganHanja,
      ganji: d.ganji,
      ganjiHanja: d.ganjiHanja,
      desc: d.desc,
      summary: d.summary || d.desc,
      traits: d.traits || [],
      keywords: d.keywords || [],
      characterImg: d.characterImg,
      ohengColor,
    };
  });
});

/** ── 관계(화살표) 목록 ── */
const relations = computed(() => {
  const src = props.hData?.relations || [];
  const cnt = personCount.value;
  return src
    .filter((r) => r.from < cnt && r.to < cnt)
    .map((r) => ({
      ...r,
      ...(RELATION_TYPES[r.type] || RELATION_TYPES.saeng),
    }));
});

/** 레이아웃 좌표 */
const positions = computed(() => {
  return LAYOUT_POSITIONS[personCount.value] || LAYOUT_POSITIONS[2];
});

/** 관계도 컨테이너 크기 (px) — Figma 기준 380×420 */
const DIAGRAM_W = 380;
const DIAGRAM_H = computed(() => (personCount.value <= 2 ? 200 : 420));
const CIRCLE_R = 52; // 원 반지름

/** 다이어그램 컨테이너 반응형 스케일 */
const diagramRef = ref(null);
const { width: diagramWidth } = useElementSize(diagramRef);
const nodeScale = computed(() => Math.min(1, (diagramWidth.value || DIAGRAM_W) / DIAGRAM_W));

/**
 * SVG 화살표 경로 계산
 * - 인접 노드: 바깥쪽으로 부드러운 곡선 (curvature 35)
 * - 맞은편 노드: 직선 (curvature 0)
 * - 아래로 나가는 화살표: 텍스트 라벨 아래에서 출발
 */
const arrowPaths = computed(() => {
  const pos = positions.value;
  const centerX = DIAGRAM_W / 2;
  const centerY = DIAGRAM_H.value / 2;
  const pad = CIRCLE_R + 14;
  /** 오행 텍스트 아래까지의 거리 (원 중심 기준) — 오행 모드에서는 글자 없으므로 짧게 */
  const TEXT_BOTTOM = CIRCLE_R + (viewMode.value === 'character' ? 55 : 30);
  const cnt = personCount.value;

  // 나(index 0) 관련 화살표를 먼저 정렬
  const sorted = [...relations.value].sort((a, b) => {
    const aHasMe = a.from === 0 || a.to === 0;
    const bHasMe = b.from === 0 || b.to === 0;
    if (aHasMe && !bHasMe) return -1;
    if (!aHasMe && bHasMe) return 1;
    return 0;
  });

  return sorted
    .map((rel, idx) => {
      const from = pos[rel.from];
      const to = pos[rel.to];
      if (!from || !to) return null;

      const fx = (from.x / 100) * DIAGRAM_W;
      const fy = (from.y / 100) * DIAGRAM_H.value;
      const tx = (to.x / 100) * DIAGRAM_W;
      const ty = (to.y / 100) * DIAGRAM_H.value;

      const dx = tx - fx;
      const dy = ty - fy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) return null;
      const nx = dx / dist;
      const ny = dy / dist;

      // 맞은편 판단 (4인: 2칸 차이)
      const idxDiff = Math.abs(rel.from - rel.to);
      const isOpposite = cnt === 4 && idxDiff === 2;

      // 양방향 체크
      const hasPeer = relations.value.some(
        (r, i) => i !== idx && r.from === rel.to && r.to === rel.from,
      );

      // 인접 노드 = 원호 기반 곡선, 맞은편 = 직선
      const isCurved = cnt >= 3 && !isOpposite;

      let cx, cy;
      if (isCurved) {
        // === 원호(arc) 기반 제어점 ===
        // 각 노드의 다이어그램 센터 기준 각도·거리
        const fromAngle = Math.atan2(fy - centerY, fx - centerX);
        const toAngle = Math.atan2(ty - centerY, tx - centerX);
        const fromR = Math.sqrt((fx - centerX) ** 2 + (fy - centerY) ** 2);
        const toR = Math.sqrt((tx - centerX) ** 2 + (ty - centerY) ** 2);
        const avgR = (fromR + toR) / 2;

        // 각도 차이 (-π ~ π 범위)
        let angleDiff = toAngle - fromAngle;
        if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

        // 중간 각도 (인접 노드 사이 바깥쪽 방향)
        let midAngle = fromAngle + angleDiff / 2;

        // peer이면 from > to 쪽은 안쪽 호로
        const isReverse = hasPeer && rel.from > rel.to;
        if (isReverse) midAngle += Math.PI;

        // 제어점 거리: 노드 원 바로 바깥을 지나는 타이트한 호
        const arcR = avgR * 1.15;

        cx = centerX + Math.cos(midAngle) * arcR;
        cy = centerY + Math.sin(midAngle) * arcR;
      } else {
        // 직선
        cx = (fx + tx) / 2;
        cy = (fy + ty) / 2;

        // 양방향이면 수직 오프셋
        if (hasPeer) {
          const perpX = -ny;
          const perpY = nx;
          const sign = rel.from > rel.to ? -1 : 1;
          cx += perpX * 20 * sign;
          cy += perpY * 20 * sign;
        }
      }

      // 시작점/끝점 계산
      let sx, sy, ex, ey;
      if (isCurved) {
        // 곡선 화살표: 제어점 방향으로 원 가장자리에서 출발
        const sDx = cx - fx;
        const sDy = cy - fy;
        const sDist = Math.sqrt(sDx * sDx + sDy * sDy);
        let sNx = sDx / sDist;
        let sNy = sDy / sDist;

        // 4인 다이아몬드 배치: 보조 성분 제거 → 원의 정확한 상/하/좌/우 가운데에서 출발
        if (cnt === 4) {
          if (Math.abs(sNx) >= Math.abs(sNy)) {
            sNy = 0;
            sNx = sNx > 0 ? 1 : -1;
          } else {
            sNx = 0;
            sNy = sNy > 0 ? 1 : -1;
          }
        }

        // 4인 + 아래 방향 → TEXT_BOTTOM 거리에서 출발 (텍스트 영역 우회), 나머지 → pad
        if (sNy > 0.7 && cnt === 4) {
          sy = fy + TEXT_BOTTOM;
          sx = fx;
        } else {
          sx = fx + sNx * pad;
          sy = fy + sNy * pad;
        }

        // 도착점: 제어점에서 도착 노드 방향
        const eDx = cx - tx;
        const eDy = cy - ty;
        const eDist = Math.sqrt(eDx * eDx + eDy * eDy);
        let eNx = eDx / eDist;
        let eNy = eDy / eDist;

        // 4인 다이아몬드 배치: 보조 성분 제거 → 원의 정확한 상/하/좌/우 가운데로 도착
        if (cnt === 4) {
          if (Math.abs(eNx) >= Math.abs(eNy)) {
            eNy = 0;
            eNx = eNx > 0 ? 1 : -1;
          } else {
            eNx = 0;
            eNy = eNy > 0 ? 1 : -1;
          }
        }

        // 4인 + 아래 방향 → TEXT_BOTTOM 거리로 도착 (텍스트 영역 우회), 나머지 → pad
        if (eNy > 0.7 && cnt === 4) {
          ey = ty + TEXT_BOTTOM;
          ex = tx;
        } else {
          ex = tx + eNx * pad;
          ey = ty + eNy * pad;
        }
      } else {
        // 직선 화살표: 대상 방향으로 원 가장자리
        sx = fx + nx * pad;
        sy = fy + ny * pad;
        ex = tx - nx * pad;
        ey = ty - ny * pad;

        // 거의 수직 아래로 나가는 화살표 → 텍스트 라벨 아래에서 출발
        if (ny > 0.85 && cnt >= 3) {
          sy = fy + TEXT_BOTTOM;
          sx = fx + (nx / ny) * TEXT_BOTTOM;
        }
        // 거의 수직 위에서 들어오는 화살표 → 텍스트 라벨 아래로 도착
        if (ny < -0.85 && cnt >= 3) {
          ey = ty + TEXT_BOTTOM;
          ex = tx + (nx / ny) * TEXT_BOTTOM;
        }

        // 맞은편 수평 화살표 → 시작/끝/제어점을 다이어그램 중심 방향으로 평행 이동 (직선 유지)
        if (isOpposite && cnt >= 3 && Math.abs(ny) < 0.3) {
          const yShift = (centerY - cy) * 0.55;
          sy += yShift;
          ey += yShift;
          cy += yShift;
        }
      }

      // 화살촉 (끝점 탄젠트 방향)
      const headLen = 10;
      const adx = ex - cx;
      const ady = ey - cy;
      const adist = Math.sqrt(adx * adx + ady * ady);
      const anx = adx / adist;
      const ay = ady / adist;

      // 비견 타입: 시작점 화살촉 (시작점 탄젠트 반대 방향)
      let tailHead = null;
      let psx = sx;
      let psy = sy;
      if (rel.type === 'bigyeon') {
        const tdx = sx - cx;
        const tdy = sy - cy;
        const tdist = Math.sqrt(tdx * tdx + tdy * tdy);
        const tnx = tdx / tdist;
        const tny = tdy / tdist;
        tailHead = `${sx},${sy} ${sx - tnx * headLen + tny * 5},${sy - tny * headLen - tnx * 5} ${sx - tnx * headLen - tny * 5},${sy - tny * headLen + tnx * 5}`;
        // path 시작점을 화살촉 길이만큼 안쪽으로 밀어 화살대가 튀어나오지 않게 함
        psx = sx - tnx * headLen;
        psy = sy - tny * headLen;
      }

      // 화살표 진행 방향에 따른 애니메이션 클래스
      const dirX = ex - sx;
      const dirY = ey - sy;
      let animDir;
      if (rel.type === 'bigyeon') {
        // 비견: 가운데에서 양쪽으로 펼치는 애니메이션
        animDir = Math.abs(dirX) >= Math.abs(dirY) ? 'center-h' : 'center-v';
      } else if (Math.abs(dirX) >= Math.abs(dirY)) {
        animDir = dirX >= 0 ? 'ltr' : 'rtl';
      } else {
        animDir = dirY >= 0 ? 'ttb' : 'btt';
      }

      // path 끝점을 화살촉 길이만큼 뒤로 당겨 화살대가 화살촉 밖으로 튀어나오지 않게 함
      const pex = ex - anx * headLen;
      const pey = ey - ay * headLen;

      return {
        ...rel,
        path: `M${psx},${psy} Q${cx},${cy} ${pex},${pey}`,
        head: `${ex},${ey} ${ex - anx * headLen + ay * 5},${ey - ay * headLen - anx * 5} ${ex - anx * headLen - ay * 5},${ey - ay * headLen + anx * 5}`,
        tailHead,
        animDir,
      };
    })
    .filter(Boolean);
});

/** 선택된 인물 인덱스 (null = 전체 표시) */
const selectedPerson = ref(null);

const selectPerson = (idx) => {
  // 같은 사람 다시 누르면 해제
  if (selectedPerson.value === idx) {
    selectedPerson.value = null;
  } else {
    selectedPerson.value = idx;
  }
  arrowKey.value++;
};

/** 화살표가 선택된 인물과 관련 있는지 */
const isArrowActive = (arrow) => {
  if (selectedPerson.value === null) return true;
  return arrow.from === selectedPerson.value || arrow.to === selectedPerson.value;
};

/** 활성 화살표만 순차 딜레이 부여 (숨겨진 화살표는 딜레이 소비하지 않음) */
const arrowDelays = computed(() => {
  let activeIdx = 0;
  return arrowPaths.value.map((arrow) => {
    if (isArrowActive(arrow)) {
      return activeIdx++ * 0.2;
    }
    return 0;
  });
});

/** 노드가 선택된 인물과 관련 있는지 */
const isNodeActive = (idx) => {
  if (selectedPerson.value === null) return true;
  if (idx === selectedPerson.value) return true;
  // 선택된 인물과 화살표로 연결된 노드
  return relations.value.some(
    (r) =>
      (r.from === selectedPerson.value && r.to === idx) ||
      (r.to === selectedPerson.value && r.from === idx),
  );
};

/** 화살표 애니메이션 키 */
const arrowKey = ref(0);
const playArrow = () => {
  selectedPerson.value = null;
  arrowKey.value++;
};

/** 바깥 클릭 시 선택 해제 */
const clearSelection = () => {
  if (selectedPerson.value !== null) {
    selectedPerson.value = null;
    arrowKey.value++;
  }
};
onMounted(() => document.addEventListener('click', clearSelection));
onUnmounted(() => document.removeEventListener('click', clearSelection));

/** 뱃지 라벨 (A, B, C, D ...) */
const getBadge = (idx) => {
  return String.fromCharCode(65 + idx);
};

// =============== 오른쪽 페이지: 항목별 궁합 + 카테고리 ===============

/** 항목별 궁합 점수 바 색상 */
const SCORE_COLORS = [
  '#e65555', // 감정 표현
  '#ff906e', // 감정 수용
  '#4fad4f', // 갈등 회복
  '#6eb9ff', // 실질 케어
  '#aa6eff', // 관계 안정
];

/** 항목별 궁합 데이터 */
const scoreItems = computed(() => {
  return props.hData?.항목별궁합 || [];
});

/** 역할구분 데이터 */
const roleInfo = computed(() => props.hData?.역할구분 || null);

/** 역할구분 — 나의역할 카드 1개 + 상대별 블록 목록 (상대카드 + 관계메시지) */
const makeRoleCard = (person, roleData, isMe) => ({
  name: person.name,
  isMe,
  characterImg: person.characterImg,
  accent: person.ohengColor.border,
  bg: person.ohengColor.bg,
  type: roleData.type,
  desc: roleData.desc,
  functions: roleData.functions,
  reason: roleData.reason,
});

const splitSummaryLines = (text) => {
  if (!text) return [];
  return String(text)
    .split(/<br\s*\/?>|\|/i)
    .map((s) => s.trim())
    .filter(Boolean);
};

const roleMeCard = computed(() => {
  const info = roleInfo.value;
  if (!info?.mine) return null;
  const me = persons.value?.[0];
  if (!me) return null;
  return makeRoleCard(me, info.mine, true);
});

const rolePartnerBlocks = computed(() => {
  const info = roleInfo.value;
  if (!info?.mine) return [];
  const [, ...partners] = persons.value;
  const partnerInfos = info.partners || [];
  return partnerInfos
    .map((p, idx) => {
      const person = partners[idx];
      if (!person) return null;
      return {
        key: `role-partner-${idx}`,
        card: makeRoleCard(person, p, false),
        summaryLines: splitSummaryLines(p.summary),
      };
    })
    .filter(Boolean);
});

const hasRoleContent = computed(() => !!roleMeCard.value && rolePartnerBlocks.value.length > 0);

/** 카테고리별 아이콘 배열 (피그마 순서 기준) */
const CATEGORY_ICON_MAP = {
  lover: [IconScales, IconPuzzle, IconFlashOn, IconSignpost, IconMagnetic],
  friend: [IconScales, IconSoccerBall, IconMusic, IconSignpost, IconTeddyBear],
  business: [IconChatBubble, IconPuzzle, IconMonitor, IconSignpost, IconMarketShare],
  family: [IconTrust, IconChatBubble, IconHeartArrow, IconSignpost, IconHighFive],
};
const ICON_LIST_DEFAULT = [IconScales, IconPuzzle, IconFlashOn, IconSignpost, IconMagnetic, IconOceanWave];

/** 현재 카테고리에 맞는 아이콘 목록 */
const currentIconList = computed(() => {
  const catKey = props.categoryId?.toLowerCase() || 'lover';
  return CATEGORY_ICON_MAP[catKey] || ICON_LIST_DEFAULT;
});

/** 카테고리 카드 목록 — 지표상세 데이터에서 동적 생성 */
const CATEGORY_ITEMS = computed(() => {
  const icons = currentIconList.value;
  const details = [
    ...(props.hData?.지표상세분석 || []),
    ...(props.hData?.관계스타일분석 || []),
  ];
  return details.map((item, idx) => ({
    id: item.id || `indicator_${idx}`,
    icon: icons[item.iconIndex ?? idx] || icons[idx] || ICON_LIST_DEFAULT[idx],
    title: item.title || '',
  }));
});

const onClickCategory = (categoryId) => {
  emit('viewCategory', categoryId);
};

// =============== 구매완료 상태: 관계타입 + 카테고리 카드 ===============

/** 오른쪽 페이지 애니메이션 상태 */
const scoreBarVisible = ref(false);

/** 게이지 애니메이션 */
const gaugeRef = ref(null);
const gaugeVisible = ref(false);
const animatedScore = ref(0);

function triggerGauge() {
  if (gaugeVisible.value) return;
  gaugeVisible.value = true;
  animateScore();
}

useIntersectionObserver(
  gaugeRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting) triggerGauge();
  },
  { threshold: 0.3 },
);

/**
 * 모바일 좌/우 페이지 전환 시 애니메이션 리셋
 * - display:none → display:block 전환 시 IntersectionObserver가 콜백을 놓칠 수 있음
 * - 왼쪽(관계도 화살표), 오른쪽(게이지) 각각 해당 사이드로 전환될 때 애니메이션 재실행
 */
watch(
  () => props.activeSide,
  (side) => {
    if (!side) return;
    if (side === 'left') {
      // 왼쪽: 화살표 애니메이션 재실행
      arrowKey.value++;
    } else if (side === 'right') {
      // 오른쪽: score bar + 게이지 + fade 애니메이션 재실행
      // display:none→block 전환 후 브라우저가 한 프레임을 렌더해야 CSS transition이 작동함
      scoreBarVisible.value = false;
      gaugeVisible.value = false;
      animatedScore.value = 0;
      nextTick(() => {
        requestAnimationFrame(() => {
          scoreBarVisible.value = true;
          triggerGauge();
        });
      });
    }
  },
);

/**
 * v-if로 재마운트되거나 최초 진입 시 오른쪽 애니메이션 트리거
 */
onMounted(() => {
  nextTick(() => {
    // score bar 최초 애니메이션
    scoreBarVisible.value = true;
    // 게이지 fallback: 이미 뷰포트에 있으면 바로 트리거
    if (gaugeRef.value && !gaugeVisible.value) {
      const rect = gaugeRef.value.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) triggerGauge();
    }
  });
});

function animateScore() {
  const target = gaugeScore.value;
  const duration = 1200;
  const start = performance.now();

  function tick(now) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
    animatedScore.value = Math.round(eased * target * 10) / 10;
    if (progress < 1) requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

/** 게이지 점수 */
const gaugeScore = computed(() => props.hData?.관계타입?.score ?? 0);

/** 게이지 레벨 — API 등급 우선, 없으면 점수 기반 계산 */
const gaugeLevel = computed(() => {
  if (props.hData?.관계타입?.level) return props.hData.관계타입.level;
  const s = gaugeScore.value;
  if (s >= 80) return '좋음';
  if (s >= 60) return '양호';
  if (s >= 40) return '보통';
  return '주의';
});

/** 게이지 아래 설명 문구 */
const gaugeDescription = computed(
  () =>
    props.hData?.관계타입?.gaugeDescription ?? '',
);

/** 관계타입 이름 */
const relationTypeName = computed(() => props.hData?.관계타입?.typeName ?? '');

/** 관계타입 부제목 */
const relationTypeSubtitle = computed(
  () => props.hData?.관계타입?.subtitle ?? '',
);

/** 관계타입 설명 */
const relationTypeDesc = computed(
  () =>
    props.hData?.관계타입?.description ?? '',
);

/** 관계타입 뱃지 */
const relationBadge = computed(() => props.hData?.관계타입?.badge ?? '');

/** 관계 분석 정보 카드 설정 */
const INFO_CARD_CONFIG = [
  { id: 'flow', icon: IconProcess, title: '관계 흐름', bg: '#e8f1fa' },
  { id: 'goodMoments', icon: IconHeart, title: '잘 맞는 순간', bg: '#fffaeb' },
  { id: 'strengths', icon: IconCheckmark, title: '장점', bg: '#eaf6ec' },
  { id: 'cautions', icon: IconCancel, title: '주의점', bg: '#fdeee7' },
  { id: 'conflictPattern', icon: IconChatBubble, title: '갈등 패턴', bg: '#f1ecf9' },
];

/** 구매완료 관계 분석 정보 카드 데이터 */
const infoCards = computed(() => {
  const cardData = props.hData?.관계분석카드;
  if (!cardData?.length) return [];
  return INFO_CARD_CONFIG.map((config) => {
    const data = cardData.find((d) => d.id === config.id);
    if (!data) return null;
    return { ...config, content: data.content };
  }).filter(Boolean);
});
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="harmony-card full-height right">
        <div class="harmony-title-bar">
          <span class="harmony-title-bar__emoji">{{ categoryLabel.emoji }}</span>
          <span class="harmony-title-bar__text">{{ categoryLabel.label }} - 기본성향</span>
        </div>
        <div class="harmony-con">
          <!-- 일러스트 + 인용문 블록 -->
          <div class="harmony-hero">
            <div class="harmony-quote">
              <div class="harmony-quote__inner">
                <QuoteIcon class="harmony-quote__mark" />
                <p class="harmony-quote__text" v-html="summaryText"></p>
                <QuoteIcon class="harmony-quote__mark harmony-quote__mark--right" />
              </div>
            </div>
            <div class="harmony-illust">
              <img
                v-if="randomImage"
                :src="randomImage"
                alt="궁합 일러스트"
                class="harmony-illust__img"
              />
            </div>
          </div>

          <!-- 토글 + 범례 -->
          <div class="harmony-toolbar">
            <div class="harmony-toolbar__left">
              <div class="harmony-toggle">
                <button
                  :class="[
                    'harmony-toggle__btn',
                    { 'harmony-toggle__btn--active': viewMode === 'character' },
                  ]"
                  @click="viewMode = 'character'"
                >
                  캐릭터
                </button>
                <button
                  :class="[
                    'harmony-toggle__btn',
                    { 'harmony-toggle__btn--active': viewMode === 'oheng' },
                  ]"
                  @click="viewMode = 'oheng'"
                >
                  오행
                </button>
              </div>
              <button class="relation-play-btn" @click="playArrow">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="#949494">
                  <path d="M4 2.5v11l9-5.5L4 2.5z" />
                </svg>
                <span class="relation-play-btn__text">관계 재생</span>
              </button>
            </div>
            <div class="harmony-legend" style="cursor: pointer" @click="openSangsaengArticle">
              <div class="harmony-legend__item">
                <LineSaeng class="harmony-legend__line" />
                <span class="harmony-legend__label" style="color: #4e80d6">생(生)</span>
              </div>
              <div class="harmony-legend__item">
                <LineGeuk class="harmony-legend__line" />
                <span class="harmony-legend__label" style="color: #ff6b6b">극(剋)</span>
              </div>
              <div class="harmony-legend__item">
                <LineBigyeon class="harmony-legend__line" />
                <span class="harmony-legend__label" style="color: #949494">비견</span>
              </div>
            </div>
          </div>

          <!-- ═══ 관계도 다이어그램 ═══ -->
          <div
            ref="diagramRef"
            class="harmony-diagram"
            :style="{ maxWidth: DIAGRAM_W + 'px', aspectRatio: DIAGRAM_W + '/' + DIAGRAM_H, '--node-scale': nodeScale }"
          >
            <!-- 인물 원형 노드 -->
            <div
              v-for="(person, idx) in persons"
              :key="'person-' + idx"
              :class="[
                'diagram-node',
                {
                  'diagram-node--dimmed': !isNodeActive(idx),
                  'diagram-node--selected': selectedPerson === idx,
                },
              ]"
              :style="{
                left: positions[idx].x + '%',
                top: positions[idx].y + '%',
              }"
              @click.stop="selectPerson(idx)"
            >
              <!-- 캐릭터 뷰 -->
              <template v-if="viewMode === 'character'">
                <div
                  class="diagram-circle"
                  :style="{
                    borderColor: person.ohengColor.border,
                    backgroundColor: '#fff',
                  }"
                >
                  <img
                    v-if="person.characterImg"
                    :src="person.characterImg"
                    alt=""
                    class="diagram-circle__img"
                  />
                </div>
              </template>
              <!-- 오행 뷰 -->
              <template v-else>
                <div
                  class="diagram-circle diagram-circle--oheng"
                  :style="{
                    borderColor: person.ohengColor.border,
                    backgroundColor: person.ohengColor.border + '1A',
                  }"
                >
                  <p class="diagram-circle__ilgan" :style="{ color: person.ohengColor.text }">
                    {{ person.ilganLabel || '' }}{{ person.oheng }}일간
                  </p>
                  <p class="diagram-circle__hanja" :style="{ color: person.ohengColor.text }">
                    ({{ person.ilganHanja || '' }}{{ OHENG_HANJA[person.oheng] || '' }})
                  </p>
                </div>
              </template>
              <div class="diagram-name" :style="{ borderColor: person.ohengColor.border }">
                <span class="diagram-name__text">{{ person.name }}</span>{{ idx === 0 ? ' (나)' : '' }}
              </div>
              <span
                v-if="viewMode === 'character'"
                class="diagram-element"
                :style="{ color: person.ohengColor.text }"
              >
                {{ person.ilganHanja || person.oheng }}
              </span>
            </div>

            <!-- SVG 화살표 오버레이 -->
            <svg
              :key="'arrows-' + arrowKey"
              class="diagram-arrows"
              :viewBox="`0 0 ${DIAGRAM_W} ${DIAGRAM_H}`"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
            >
              <g
                v-for="(arrow, aIdx) in arrowPaths"
                :key="'arrow-' + aIdx"
                :class="[
                  'diagram-arrow-group',
                  isArrowActive(arrow) ? 'arrow-' + arrow.animDir : 'arrow-hidden',
                ]"
                :style="{ animationDelay: isArrowActive(arrow) ? arrowDelays[aIdx] + 's' : '0s' }"
              >
                <path
                  :d="arrow.path"
                  :stroke="arrow.color"
                  stroke-width="2"
                  fill="none"
                  :stroke-dasharray="arrow.lineStyle === 'dashed' ? '6,4' : 'none'"
                />
                <polygon :points="arrow.head" :fill="arrow.color" />
                <!-- 비견: 시작점 화살촉 -->
                <polygon v-if="arrow.tailHead" :points="arrow.tailHead" :fill="arrow.color" />
              </g>
            </svg>
          </div>

          <!-- ═══ 인물 정보 카드 ═══ -->
          <div class="harmony-persons">
            <div
              v-for="(person, idx) in persons"
              :key="'info-' + idx"
              class="harmony-person-card"
              :style="{
                '--card-accent': person.ohengColor.border,
                '--card-bg': person.ohengColor.bg,
              }"
            >
              <!-- 헤더: 뱃지 + 이름 | 나이 + 생년월일 -->
              <div class="harmony-person-card__header">
                <div class="harmony-person-card__header-left">
                  <span
                    class="harmony-person-card__badge"
                    :style="{ background: person.ohengColor.border }"
                  >
                    {{ getBadge(idx) }}
                  </span>
                  <span class="harmony-person-card__name">
                    <span class="harmony-person-card__name-text">{{ person.name }}</span>
                    <span class="harmony-person-card__name-suffix">
                      님 ({{ person.gender === '남' ? '男' : '女' }})
                    </span>
                  </span>
                </div>
                <div class="harmony-person-card__header-right">
                  <span class="harmony-person-card__meta">{{ person.age }}</span>
                  <span class="harmony-person-card__meta">{{ person.birthInfo }}</span>
                </div>
              </div>

              <!-- 간지·오행 + 한줄 요약 -->
              <div class="harmony-person-card__info">
                <p
                  v-if="person.ganji || person.oheng"
                  class="harmony-person-card__ganji"
                >
                  {{ person.ganji }}{{ person.ganjiHanja ? `(${person.ganjiHanja})` : '' }}
                  · {{ person.oheng }}({{ OHENG_HANJA[person.oheng] || '' }})
                </p>
                <p v-if="person.summary" class="harmony-person-card__summary" v-html="person.summary">
                </p>
              </div>

              <!-- 특성 목록 -->
              <div v-if="person.traits?.length" class="harmony-person-card__traits">
                <p v-for="(trait, tIdx) in person.traits" :key="tIdx" v-html="'· ' + trait">
                </p>
              </div>

              <!-- 키워드 태그 -->
              <div v-if="person.keywords?.length" class="harmony-person-card__keywords">
                <span
                  v-for="(kw, kIdx) in person.keywords"
                  :key="kIdx"
                  class="harmony-person-card__tag"
                >
                  {{ kw }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 페이지: 항목별 궁합 + 카테고리 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="harmony-card full-height left">
        <div class="harmony-right">
          <!-- 항목별 궁합 -->
          <div class="score-card">
            <p class="score-card__title">
              <span class="score-card__emoji">📊</span>
              항목별 궁합
            </p>
            <div class="score-list">
              <div v-for="(item, idx) in scoreItems" :key="idx" class="score-item">
                <span class="score-item__label">{{ item.label }}</span>
                <div class="score-item__bar-wrap">
                  <div class="score-item__bar-bg" />
                  <div
                    class="score-item__bar-fill"
                    :style="{
                      width: scoreBarVisible ? item.score + '%' : '0%',
                      backgroundColor: SCORE_COLORS[idx] || '#ccc',
                    }"
                  />
                </div>
                <span class="score-item__value">{{ item.score }}</span>
              </div>
            </div>
          </div>

          <!-- 구매완료 상태 -->
          <template v-if="hasPermission">
            <!-- 우리의 관계 타입 -->
            <div ref="gaugeRef" class="relation-section">
              <p class="relation-section__title">우리의 관계 타입</p>

              <!-- 반원 게이지 차트 -->
              <div class="gauge-wrap">
                <svg viewBox="0 0 200 110" class="gauge-svg" overflow="visible">
                  <defs>
                    <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="#E84A31" />
                      <stop offset="35%" stop-color="#E8A631" />
                      <stop offset="65%" stop-color="#7BC74A" />
                      <stop offset="100%" stop-color="#34C759" />
                    </linearGradient>
                  </defs>
                  <!-- 배경 호 -->
                  <path
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    stroke="#ebebeb"
                    stroke-width="16"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <!-- 채워지는 호 -->
                  <path
                    class="gauge-fill"
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    stroke="url(#gaugeGrad)"
                    stroke-width="16"
                    fill="none"
                    stroke-linecap="round"
                    :stroke-dasharray="251.33"
                    :stroke-dashoffset="gaugeVisible ? 251.33 * (1 - gaugeScore / 100) : 251.33"
                  />
                  <!-- 퍼센트 (반원 꼭대기 고정, 흰색) -->
                  <text
                    x="103"
                    y="21"
                    text-anchor="middle"
                    dominant-baseline="central"
                    fill="white"
                    font-size="15"
                    font-weight="600"
                    font-family="Pretendard, sans-serif"
                  >
                    {{ animatedScore }}%
                  </text>
                  <!-- 레벨 (반원 내부 중앙 고정) -->
                  <text
                    x="100"
                    y="80"
                    text-anchor="middle"
                    fill="#333"
                    font-size="20"
                    font-weight="700"
                    font-family="Pretendard, sans-serif"
                    letter-spacing="2"
                    :class="{ 'gauge-level-visible': gaugeVisible }"
                    class="gauge-level"
                  >
                    {{ gaugeLevel }}
                  </text>
                </svg>
              </div>

              <!-- 설명 문구 -->
              <p
                class="relation-section__desc relation-fade"
                :class="{ 'relation-fade--visible': gaugeVisible }"
                v-html="gaugeDescription"
              >
              </p>

              <!-- 관계 뱃지 -->
              <div
                class="relation-badge relation-fade"
                :class="{ 'relation-fade--visible': gaugeVisible }"
              >
                <span class="relation-badge__text" v-html="relationBadge"></span>
              </div>

              <!-- 관계타입 카드 (2_관계타입 데이터가 있을 때만 표시) -->
              <div
                v-if="relationTypeName"
                class="relation-type-card relation-fade"
                :class="{ 'relation-fade--visible': gaugeVisible }"
              >
                <div class="relation-type-card__name-wrap">
                  <span class="relation-type-card__highlight" />
                  <p class="relation-type-card__name" v-html="relationTypeName"></p>
                </div>
                <p class="relation-type-card__subtitle" v-html="relationTypeSubtitle"></p>
                <p class="relation-type-card__desc" v-html="relationTypeDesc"></p>
              </div>
            </div>

            <!-- 역할구분 -->
            <div v-if="hasRoleContent" class="role-section">
              <p class="role-section__title">
                <IconDecision class="role-section__icon" />
                역할구분
              </p>

              <!-- 나의역할 -->
              <div
                v-if="roleMeCard"
                class="role-card role-card--me"
                :style="{ '--role-accent': roleMeCard.accent }"
              >
                <div class="role-card__head">
                  <div class="role-card__avatar" :style="{ borderColor: roleMeCard.accent }">
                    <img
                      v-if="roleMeCard.characterImg"
                      :src="roleMeCard.characterImg"
                      :alt="roleMeCard.name"
                      class="role-card__avatar-img"
                    />
                  </div>
                  <div class="role-card__head-text">
                    <p class="role-card__name">
                      <span class="role-card__name-text">{{ roleMeCard.name }}</span>
                      <span class="role-card__name-suffix">님 (나)</span>
                    </p>
                    <p class="role-card__type" :style="{ color: roleMeCard.accent }">
                      {{ roleMeCard.type }}
                    </p>
                  </div>
                </div>

                <dl v-if="roleMeCard.functions" class="role-traits">
                  <div class="role-traits__row">
                    <dt class="role-traits__label role-traits__label--function">위치</dt>
                    <dd class="role-traits__text" v-html="roleMeCard.functions.위치"></dd>
                  </div>
                  <div class="role-traits__row">
                    <dt class="role-traits__label role-traits__label--contrib">기여</dt>
                    <dd class="role-traits__text" v-html="roleMeCard.functions.기여"></dd>
                  </div>
                  <div class="role-traits__row">
                    <dt class="role-traits__label role-traits__label--caution">유의</dt>
                    <dd class="role-traits__text" v-html="roleMeCard.functions.유의"></dd>
                  </div>
                </dl>
                <p
                  v-else-if="roleMeCard.desc"
                  class="role-card__desc"
                  v-html="roleMeCard.desc"
                ></p>

                <span v-if="roleMeCard.reason" class="role-card__reason">{{ roleMeCard.reason }}</span>
              </div>

              <!-- 상대별 블록 -->
              <div
                v-for="block in rolePartnerBlocks"
                :key="block.key"
                class="role-partner-block"
              >
                <div class="role-partner-arrow" aria-hidden="true">
                  <span class="role-partner-arrow__line" />
                  <span class="role-partner-arrow__label">with</span>
                  <span class="role-partner-arrow__line" />
                </div>

                <div
                  class="role-card"
                  :style="{ '--role-accent': block.card.accent }"
                >
                  <div class="role-card__head">
                    <div
                      class="role-card__avatar"
                      :style="{ borderColor: block.card.accent }"
                    >
                      <img
                        v-if="block.card.characterImg"
                        :src="block.card.characterImg"
                        :alt="block.card.name"
                        class="role-card__avatar-img"
                      />
                    </div>
                    <div class="role-card__head-text">
                      <p class="role-card__name">
                        <span class="role-card__name-text">{{ block.card.name }}</span>
                        <span class="role-card__name-suffix">님</span>
                      </p>
                      <p class="role-card__type" :style="{ color: block.card.accent }">
                        {{ block.card.type }}
                      </p>
                    </div>
                  </div>

                  <dl v-if="block.card.functions" class="role-traits">
                    <div class="role-traits__row">
                      <dt class="role-traits__label role-traits__label--function">위치</dt>
                      <dd class="role-traits__text" v-html="block.card.functions.위치"></dd>
                    </div>
                    <div class="role-traits__row">
                      <dt class="role-traits__label role-traits__label--contrib">기여</dt>
                      <dd class="role-traits__text" v-html="block.card.functions.기여"></dd>
                    </div>
                    <div class="role-traits__row">
                      <dt class="role-traits__label role-traits__label--caution">유의</dt>
                      <dd class="role-traits__text" v-html="block.card.functions.유의"></dd>
                    </div>
                  </dl>
                  <p
                    v-else-if="block.card.desc"
                    class="role-card__desc"
                    v-html="block.card.desc"
                  ></p>

                  <span v-if="block.card.reason" class="role-card__reason">{{ block.card.reason }}</span>
                </div>

                <div v-if="block.summaryLines.length" class="role-relation">
                  <p class="role-relation__title">
                    <span class="role-relation__dot" />
                    {{ roleMeCard?.name }} × {{ block.card.name }} 관계 메시지
                  </p>
                  <ul class="role-relation__list">
                    <li
                      v-for="(line, lIdx) in block.summaryLines"
                      :key="lIdx"
                      class="role-relation__item"
                      v-html="line"
                    ></li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 관계 분석 정보 카드 (2_관계타입 데이터가 있을 때만 표시) -->
            <div v-if="infoCards.length" class="info-cards">
              <div
                v-for="card in infoCards"
                :key="card.id"
                class="info-card"
                :style="{ backgroundColor: card.bg }"
              >
                <div class="info-card__header">
                  <img :src="card.icon" :alt="card.title" class="info-card__icon" />
                  <p class="info-card__title">{{ card.title }}</p>
                </div>
                <p
                  v-for="(line, lineIdx) in card.content"
                  :key="lineIdx"
                  class="info-card__text"
                  v-html="line"
                >
                </p>
              </div>
            </div>
          </template>

          <!-- 미구매 상태: 카테고리 카드 -->
          <template v-else>
            <div class="harmony-category-card-wrapper">
              <div v-for="cat in CATEGORY_ITEMS" :key="cat.id" class="harmony-category-card">
                <div class="harmony-category-card__left">
                  <img :src="cat.icon" :alt="cat.title" class="harmony-category-card__icon" />
                  <span class="harmony-category-card__title">{{ cat.title }}</span>
                </div>
                <span class="harmony-category-card__lock">🔒</span>
              </div>
            </div>
          </template>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

.harmony-card {
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0;
  overflow-y: auto;

  &.right {
    border-right: 1px solid var(--Gray-300, #ebebeb);
  }

  &.left {
    border-left: 1px solid var(--Gray-300, #ebebeb);
  }
}

.harmony-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: #fff8f5;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;

  &__emoji {
    font-size: 16px;
    line-height: 18px;
    text-align: center;
  }

  &__text {
    color: #ff453a;
    line-height: 24px;
  }
}

.harmony-con {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px;
}

/* ── 히어로 (일러스트 + 인용문) ── */
.harmony-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff8f5;
  border-radius: 12px;
  width: 100%;
  padding-top: 25px;
  padding-bottom: 30px;
}

.harmony-illust {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  &__img {
    width: 242px;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }
}

.harmony-quote {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 26px 15px;
  width: 100%;
  box-sizing: border-box;

  &__inner {
    display: flex;
    align-items: start;
    gap: 20px;
    padding: 3px 0;
    max-width: 100%;
  }

  &__mark {
    width: 18px;
    height: 14px;
    flex-shrink: 0;

    &--right {
      transform: rotate(180deg);
    }
  }

  &__text {
    color: var(--Gray-800, #333);
    font-family: Pretendard, sans-serif;
    font-size: 20px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    margin: 0;
    min-width: 0;
  }
}

/* ── 토글 + 범례 ── */
.harmony-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  &__left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.harmony-toggle {
  display: flex;
  align-items: center;
  gap: 2px;
  background: #f8f8f8;
  border-radius: 30px;

  &__btn {
    padding: 8px 10px;
    border: none;
    background: transparent;
    border-radius: 20px;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 18px;
    color: #949494;
    cursor: pointer;
    white-space: nowrap;

    &--active {
      background: #ff6b6b;
      color: #fff;
      font-weight: 700;
    }
  }
}

.harmony-legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #ebebeb;
  border-radius: 6px;
  padding: 5px 15px;
  background: #fff;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: none;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__line {
    width: 31px;
    height: 8px;
    flex-shrink: 0;
  }

  &__label {
    font-family: Pretendard, sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }
}

/* ── 관계도 다이어그램 ── */
.harmony-diagram {
  position: relative;
  width: 100%;
  margin: 0 auto 10px;
  overflow: visible;
}

.diagram-node {
  position: absolute;
  /* X: 가로 중앙 정렬, Y: 원 반지름(52px)만큼 올려서 원 중심 = position 좌표 */
  --s: var(--node-scale, 1);
  transform: translate(-50%, calc(-52px * var(--s))) scale(var(--s));
  transform-origin: center top;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  z-index: 2;
  cursor: pointer;
  transition:
    opacity 0.3s,
    transform 0.3s;

  &--dimmed {
    opacity: 0.25;
  }

  &--selected {
    transform: translate(-50%, calc(-52px * var(--s))) scale(calc(var(--s) * 1.08));
    z-index: 3;
  }

  &:hover:not(&--dimmed) {
    transform: translate(-50%, calc(-54px * var(--s))) scale(calc(var(--s) * 1.05));
  }

  &:active:not(&--dimmed) {
    transform: translate(-50%, calc(-52px * var(--s))) scale(calc(var(--s) * 0.97));
  }
}

.diagram-circle {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  border: 3px solid #ccc;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--oheng {
    flex-direction: column;
    gap: 0;
  }

  &__ilgan {
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
    text-align: center;
  }

  &__hanja {
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
    text-align: center;
  }
}

.diagram-name {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border: 2px solid #ccc;
  border-radius: 100px;
  background: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #575757;
  white-space: nowrap;
  max-width: 160px;
  margin-top: -13px;
  position: relative;
  z-index: 1;

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
  }
}

.diagram-element {
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  margin-top: 4px;
}

/* SVG 화살표 오버레이 */
.diagram-arrows {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

/* 비활성 화살표 숨김 */
.arrow-hidden {
  opacity: 0;
  transition: opacity 0.3s;
}

/* 화살표 방향별 애니메이션 */
.arrow-ltr {
  animation: arrow-ltr 0.35s ease-out forwards;
  clip-path: inset(0 100% 0 0);
}
.arrow-rtl {
  animation: arrow-rtl 0.35s ease-out forwards;
  clip-path: inset(0 0 0 100%);
}
.arrow-ttb {
  animation: arrow-ttb 0.35s ease-out forwards;
  clip-path: inset(0 0 100% 0);
}
.arrow-btt {
  animation: arrow-btt 0.35s ease-out forwards;
  clip-path: inset(100% 0 0 0);
}

@keyframes arrow-ltr {
  from {
    clip-path: inset(0 100% 0 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
@keyframes arrow-rtl {
  from {
    clip-path: inset(0 0 0 100%);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
@keyframes arrow-ttb {
  from {
    clip-path: inset(0 0 100% 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
@keyframes arrow-btt {
  from {
    clip-path: inset(100% 0 0 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}

/* 비견: 가운데에서 양쪽으로 펼치는 애니메이션 */
.arrow-center-h {
  animation: arrow-center-h 0.35s ease-out forwards;
  clip-path: inset(0 50% 0 50%);
}
.arrow-center-v {
  animation: arrow-center-v 0.35s ease-out forwards;
  clip-path: inset(50% 0 50% 0);
}

@keyframes arrow-center-h {
  from {
    clip-path: inset(0 50% 0 50%);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
@keyframes arrow-center-v {
  from {
    clip-path: inset(50% 0 50% 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}

.relation-play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #ebebeb;
  border-radius: 20px;
  background: #fff;
  cursor: pointer;
  transition:
    background 0.2s,
    transform 0.2s;

  &:hover {
    background: #f5f5f5;
    transform: scale(1.03);
  }

  &:active {
    transform: scale(0.97);
  }

  &__text {
    font-family: Pretendard, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #949494;
    line-height: 1;
  }
}

/* ── 인물 정보 ── */
.harmony-persons {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.harmony-person-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  border: 1px solid var(--card-accent, #ccc);
  border-radius: 12px;
  padding: 16px;
  background: var(--card-bg, #f5f5f5);
  font-family: Pretendard, sans-serif;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__badge {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
    flex-shrink: 0;
  }

  &__name {
    display: flex;
    align-items: baseline;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
  }

  &__name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 0 1 auto;
  }

  &__name-suffix {
    flex-shrink: 0;
    margin-left: 2px;
  }

  &__meta {
    font-size: 14px;
    font-weight: 400;
    color: #575757;
    line-height: 1.3;
    white-space: nowrap;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 15px;
    line-height: 24px;
  }

  &__ganji {
    margin: 0;
    font-weight: 400;
    color: #757575;
  }

  &__summary {
    margin: 0;
    font-weight: 500;
    color: #575757;
  }

  &__traits {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;

    p {
      margin: 0;
    }
  }

  &__keywords {
    display: flex;
    gap: 5px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: var(--card-accent, #333);
    background: color-mix(in srgb, var(--card-accent, #333) 10%, transparent);
    white-space: nowrap;
  }
}

/* ═══════════════════════════════════════
   오른쪽 페이지: 항목별 궁합 + 카테고리
   ═══════════════════════════════════════ */
.harmony-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 24px 24px 50px;
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
}

.score-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  padding: 15px 15px 20px;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #333;
  }

  &__emoji {
    font-size: 15px;
  }
}

.score-list {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px 8px;
  align-items: center;
  width: 100%;
}

.score-item {
  display: contents;

  &__label {
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: #575757;
    white-space: nowrap;
  }

  &__bar-wrap {
    position: relative;
    height: 10px;
  }

  &__bar-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #ebebeb;
    border-radius: 10px;
  }

  &__bar-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: 10px;
    transition: width 0.6s ease-out;
  }

  &__value {
    flex-shrink: 0;
    min-width: 36px;
    font-family: Pretendard, sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: #757575;
    text-align: right;
    white-space: nowrap;
  }
}

/* ── 역할구분 ── */
.role-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #fdf2e7;
  border-radius: 12px;
  box-sizing: border-box;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.1;
    color: #333;
  }

  &__icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
}

.role-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  border-left: 3px solid var(--role-accent, #ebebeb);

  &--me {
    background: #fffdf8;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__head-text {
    flex: 0 0 0;
    min-width: 0;
    white-space: nowrap;
    overflow: visible;
  }

  &__avatar {
    width: 74px;
    height: 74px;
    border-radius: 50%;
    border: 2px solid #ccc;
    overflow: hidden;
    flex-shrink: 0;
    background: #fff;
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__head-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  &__name {
    display: flex;
    align-items: baseline;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    color: #333;
    max-width: 140px;
    white-space: nowrap;
  }

  &__name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 0 1 auto;
  }

  &__name-suffix {
    flex-shrink: 0;
    margin-left: 2px;
  }

  &__type {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: #333;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__desc {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #333;
  }

  &__reason {
    align-self: flex-start;
    padding: 3px 6px;
    background: var(--Gray-200, #F8F8F8);
    border-radius: 4px;
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: #575757;
  }
}

.role-traits {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  background: var(--Gray-200, #F8F8F8);
  border-radius: 8px;

  &__row {
    display: grid;
    grid-template-columns: 52px 1fr;
    gap: 10px;
    align-items: start;
    margin: 0;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 11px;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    white-space: nowrap;

    &--function {
      background: #4e80d6;
    }

    &--contrib {
      background: #34a853;
    }

    &--caution {
      background: #e8a631;
    }
  }

  &__text {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    color: #333;
  }

  @media (max-width: 768px) {
    &__row {
      grid-template-columns: 1fr;
      gap: 6px;
    }

    &__label {
      justify-self: start;
    }
  }
}

.role-partner-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.role-partner-arrow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px;

  &__line {
    flex: 1;
    height: 1px;
    background: rgba(180, 122, 55, 0.3);
  }

  &__label {
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #b47a37;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
}

.role-relation {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  border: 1px dashed rgba(180, 122, 55, 0.35);

  &__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: #333;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #575757;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__item {
    position: relative;
    padding-left: 14px;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    color: #575757;

    &::before {
      content: '';
      position: absolute;
      left: 4px;
      top: 9px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #b47a37;
    }

    :deep(strong) {
      color: #333;
      font-weight: 700;
    }
  }
}

.harmony-category-card-wrapper {
  width: 100%;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
}

.harmony-category-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border-bottom: 1px solid #ebebeb;
  background: #fff;

  &:last-child {
    border-bottom: none;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    overflow: hidden;
  }

  &__icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__title {
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__lock {
    flex-shrink: 0;
    font-size: 15px;
    line-height: 16px;
  }
}

/* ═══════════════════════════════════════
   구매완료 상태: 관계타입 + 카테고리 그리드
   ═══════════════════════════════════════ */
.relation-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  background: #fff8f5;
  border-radius: 12px;
  padding: 20px 16px;

  &__title {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.5;
    color: #333;
    text-align: center;
    width: 100%;
  }

  &__desc {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    text-align: center;
    white-space: pre-line;
  }
}

.gauge-wrap {
  width: 272px;
  margin: 0 auto;
  overflow: visible;
}

.gauge-svg {
  width: 100%;
  height: auto;
}

.gauge-fill {
  transition: stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.gauge-level {
  opacity: 0;
  transition: opacity 0.5s ease 0.8s;
}

.gauge-level-visible {
  opacity: 1;
}

.relation-fade {
  opacity: 0;
  transform: translateY(16px);
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
}

.relation-fade--visible {
  opacity: 1;
  transform: translateY(0);
}

.relation-section__desc.relation-fade {
  transition-delay: 0.6s;
}

.relation-badge.relation-fade {
  transition-delay: 0.8s;
}

.relation-type-card.relation-fade {
  transition-delay: 1s;
}

.relation-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 14px;
  background: rgba(64, 168, 36, 0.1);
  border: 1px solid #40a824;
  border-radius: 100px;

  &__text {
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    color: #40a824;
    text-align: center;
  }
}

.relation-type-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #fff;
  border-radius: 12px;

  &__name-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &__highlight {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 142px;
    height: 16px;
    background: rgba(255, 107, 107, 0.15);
  }

  &__name {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: #333;
    text-align: center;
    position: relative;
    z-index: 1;
  }

  &__subtitle {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    text-align: center;
  }

  &__desc {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    text-align: center;
    white-space: pre-line;
  }
}

.info-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.info-card {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;

  &__header {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__title {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.1;
    color: #333;
  }

  &__text {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
  }
}
</style>
